<script lang="ts">
  import '../app.css';
  import '@xyflow/svelte/dist/style.css';
  import * as Sentry from '@sentry/sveltekit';
  import { afterNavigate } from '$app/navigation';
  import { page } from '$app/state';
  import { AUTH_ENABLED } from '$lib/auth/config';
  import {
    restoreSession,
    login,
    logout,
    isAuthenticated,
    getAccessToken,
    getUserName,
  } from '$lib/auth/auth.svelte';
  import { connection } from '$lib/api/client';
  import { fetchIdentity } from '$lib/stores/identity.svelte';
  import { initTheme } from '$lib/stores/theme.svelte';
  import ErrorBoundary from '$lib/components/errors/ErrorBoundary.svelte';
  import ErrorToasts from '$lib/components/errors/ErrorToasts.svelte';
  import { onMount } from 'svelte';

  let { children } = $props();

  onMount(() => { initTheme(); });

  afterNavigate((nav) => {
    Sentry.addBreadcrumb({
      category: 'navigation',
      type: 'navigation',
      data: {
        from: nav.from?.url?.pathname,
        to: nav.to?.url?.pathname,
        type: nav.type,
      },
    });
  });

  if (AUTH_ENABLED) {
    restoreSession();
    // Sync token to connection immediately (before child onMount fires)
    const restored = getAccessToken();
    if (restored) connection.headers['Authorization'] = `Bearer ${restored}`;
  }

  // Keep in sync reactively for login/logout
  $effect(() => {
    if (!AUTH_ENABLED) {
      fetchIdentity();
      return;
    }
    const token = getAccessToken();
    if (token) {
      connection.headers['Authorization'] = `Bearer ${token}`;
      fetchIdentity();
    } else {
      delete connection.headers['Authorization'];
    }
  });

  const isCallbackRoute = $derived(page.url.pathname === '/auth/callback');
  const showApp = $derived(!AUTH_ENABLED || isAuthenticated() || isCallbackRoute);
</script>

<svelte:head>
  <title>Helinks · STS Wand</title>
</svelte:head>

{#if showApp}
  <ErrorBoundary
    label="application render failed"
    tags={{ component: '+layout', action: 'render' }}
    scope="page"
  >
    {@render children()}
  </ErrorBoundary>
{:else}
  <div class="login-screen">
    <div class="login-card">
      <img class="helinks-wordmark" src="/helinks-wordmark.png" alt="Helinks" />
      <h1>
        STS Wand
        <img class="wand-mark" src="/sts-wand.svg" alt="" />
      </h1>
      <button class="login-btn" onclick={login}>Sign in with Helinks Account</button>
    </div>
  </div>
{/if}

<ErrorToasts />

<style>
  .login-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    width: 100vw;
  }
  .login-card {
    text-align: center;
    padding: var(--sp-12);
    background: var(--bg-secondary);
    border: 1px solid var(--border-node);
    border-radius: var(--radius-xl);
  }
  .helinks-wordmark {
    display: block;
    width: 220px;
    height: auto;
    margin: 0 auto var(--sp-3);
  }
  .login-card h1 {
    margin: 0 0 var(--sp-4);
    font-size: var(--fs-xl);
    font-weight: 500;
    color: var(--text-secondary);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--sp-2);
  }
  .wand-mark {
    width: 1.4em;
    height: 1.4em;
    border-radius: var(--radius-sm);
  }
  .login-btn {
    background: var(--accent-data);
    color: #fff;
    border: none;
    padding: 10px var(--sp-6);
    border-radius: var(--radius-md);
    font-size: var(--fs-md);
  }
  .login-btn:hover {
    filter: brightness(1.15);
  }
</style>
